import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import formService from '../../services/formService'
import optionService from '../../services/optionService'
import {
  HiOutlinePlus, HiOutlineX, HiOutlinePencil, HiOutlineTrash,
  HiOutlineClipboardCopy, HiOutlineExternalLink, HiOutlineBriefcase,
} from 'react-icons/hi'
import toast from 'react-hot-toast'

const publicUrl = (slug) => `${window.location.origin}/tuyen-dung/${slug}`

function FormBuilderManagement() {
  const navigate = useNavigate()
  const [forms, setForms] = useState([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      const res = await formService.list()
      if (res.status === 'success') setForms(res.data)
    } finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])

  const copyLink = (slug) => {
    navigator.clipboard?.writeText(publicUrl(slug))
    toast.success('Đã copy link form')
  }

  const remove = async (f) => {
    if (!window.confirm(`Xóa chiến dịch "${f.title}"? (Hồ sơ đã nhận vẫn giữ lại)`)) return
    try { await formService.remove(f.id); toast.success('Đã xóa'); load() } catch { /* interceptor */ }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-3">
        <div>
          <h1 className="page-title">Chiến dịch tuyển dụng</h1>
          <p className="page-subtitle">Mỗi chiến dịch 1 URL riêng. <b>Hồ sơ nộp qua link của bạn tự về tay bạn</b> (bạn là Người liên hệ) — không cần chờ phân luồng.</p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-primary">
          <HiOutlinePlus className="w-5 h-5" /> Tạo chiến dịch
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="spinner w-8 h-8" /></div>
      ) : forms.length === 0 ? (
        <div className="card p-12 text-center text-secondary-400">
          Chưa có chiến dịch nào. Bấm “Tạo chiến dịch” để tạo form tuyển dụng riêng (vd tuyển Giám đốc).
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {forms.map((f) => (
            <div key={f.id} className="card p-5 flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-semibold text-secondary-900 truncate">{f.title}</h3>
                  {f.position && (
                    <span className="inline-flex items-center gap-1 text-2xs text-accent-700 bg-accent-50 px-2 py-0.5 rounded-full mt-1">
                      <HiOutlineBriefcase className="w-3 h-3" /> {f.position}
                    </span>
                  )}
                </div>
                {f.is_active
                  ? <span className="badge-success shrink-0">Đang mở</span>
                  : <span className="badge-neutral shrink-0">Đã đóng</span>}
              </div>

              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div className="bg-secondary-50 rounded-lg py-1.5">
                  <div className="text-base font-bold text-secondary-800">{f.view_count ?? 0}</div>
                  <div className="text-2xs text-secondary-400">👁 Lượt xem</div>
                </div>
                <div className="bg-secondary-50 rounded-lg py-1.5">
                  <div className="text-base font-bold text-primary-600">{f.submit_count ?? 0}</div>
                  <div className="text-2xs text-secondary-400">📥 Hồ sơ</div>
                </div>
                <div className="bg-secondary-50 rounded-lg py-1.5">
                  <div className="text-base font-bold text-emerald-600">{f.conversion ?? 0}%</div>
                  <div className="text-2xs text-secondary-400">⚡ Chuyển đổi</div>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2 text-2xs text-secondary-400">
                <span className="px-2 py-0.5 bg-secondary-100 rounded">/{f.slug}</span>
                <span>· {f.field_count} trường</span>
                {f.creator_name && <span>· 👤 {f.creator_name}</span>}
              </div>

              <div className="mt-4 pt-3 border-t border-secondary-50 flex items-center gap-1">
                <button onClick={() => copyLink(f.slug)} className="btn-secondary btn-sm flex-1" title="Copy link">
                  <HiOutlineClipboardCopy className="w-4 h-4" /> Copy link
                </button>
                <a href={publicUrl(f.slug)} target="_blank" rel="noreferrer" className="p-2 text-secondary-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg" title="Mở form">
                  <HiOutlineExternalLink className="w-4 h-4" />
                </a>
                <button onClick={() => navigate(`/admin/forms/${f.id}`)} className="p-2 text-secondary-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg" title="Sửa">
                  <HiOutlinePencil className="w-4 h-4" />
                </button>
                <button onClick={() => remove(f)} className="p-2 text-secondary-500 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Xóa">
                  <HiOutlineTrash className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && <CreateModal onClose={() => setCreating(false)} onCreated={(id) => navigate(`/admin/forms/${id}`)} />}
    </div>
  )
}

function CreateModal({ onClose, onCreated }) {
  const [title, setTitle] = useState('')
  const [position, setPosition] = useState('')
  const [positions, setPositions] = useState([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    optionService.getPublic().then((r) => { if (r.status === 'success') setPositions(r.data.position || []) }).catch(() => {})
  }, [])

  const save = async () => {
    if (!title.trim()) { toast.error('Nhập tiêu đề chiến dịch'); return }
    setSaving(true)
    try {
      const res = await formService.create({ title: title.trim(), position: position.trim() })
      if (res.status === 'success') { toast.success('Đã tạo chiến dịch'); onCreated(res.data.id) }
    } catch { /* interceptor */ } finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative card w-full max-w-md p-6 animate-scale-in">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-secondary-900">Tạo chiến dịch tuyển dụng</h3>
          <button onClick={onClose} className="p-1.5 text-secondary-400 hover:text-secondary-700"><HiOutlineX className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="label">Tiêu đề <span className="text-red-500">*</span></label>
            <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="VD: Tuyển dụng Giám đốc Kinh doanh" />
          </div>
          <div>
            <label className="label">Vị trí mục tiêu</label>
            <select className="select" value={position} onChange={(e) => setPosition(e.target.value)}>
              <option value="">— Chọn vị trí —</option>
              {positions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              {position && !positions.some((o) => o.value === position) && <option value={position}>{position} (cũ)</option>}
            </select>
            <p className="text-2xs text-secondary-400 mt-1">Đồng bộ với <a href="/admin/options" target="_blank" rel="noreferrer" className="text-primary-600 underline">Cấu hình form → Vị trí</a>. Hồ sơ nộp sẽ tự gắn vị trí này.</p>
          </div>
          <button onClick={save} disabled={saving} className="btn-primary w-full mt-1">
            {saving ? 'Đang tạo...' : 'Tạo & cấu hình trường'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default FormBuilderManagement
